import { useEffect, useRef, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSearchbar,
  IonSpinner,
  IonTitle,
  IonToggle,
  IonToolbar,
} from "@ionic/react";
import { SubModel } from "model";
import { Alert, Colors, Icon } from "swing-components";

import { translate } from "~utils";

import { useSubs } from "./hooks";
import type { SectionItemProps, SectionProps, SubSelectModalProps } from "./types";

const Badge = ({ label }: { label: string }) => (
  <IonBadge slot="end" css={{ backgroundColor: "var(--ion-color-medium-tint)" }}>
    {label}
  </IonBadge>
);

function SectionItem(props: SectionItemProps) {
  const { sub, onChange, isChecked, extraData } = props;
  const isDisabled = !sub.isEligible || (extraData?.allowCredOnly && !sub.isCredentialed);
  return (
    <IonItem>
      <IonCheckbox
        value={sub}
        checked={isChecked(sub)}
        onIonChange={onChange}
        justify="start"
        labelPlacement="end"
        disabled={isDisabled}
      >
        {sub.name}
      </IonCheckbox>
      {/* Badge or Credential Icon */}
      {!sub.isEligible ? (
        <Badge label={translate("SUB_SELECT_LABEL_UNAVAILABLE")} />
      ) : sub.isCredentialed ? (
        <div slot="end">
          <Icon name="Verified" color={Colors.green500} />
        </div>
      ) : (
        <Badge label={translate("SUB_SELECT_LABEL_NON_CRED")} />
      )}
    </IonItem>
  );
}

function Section(props: SectionProps) {
  const { data, header } = props;

  return (
    <>
      <IonItem lines="full">
        <h3>{header}</h3>
      </IonItem>
      {!data.length ? (
        <IonItem>
          <div className="italic">{translate("LABEL_NONE")}</div>
        </IonItem>
      ) : (
        <IonList className="ion-no-padding">
          {data.map((sub) => (
            <SectionItem key={`${props.header.toLowerCase()}-${sub.id}`} sub={sub} {...props} />
          ))}
        </IonList>
      )}
    </>
  );
}

export function SubSelectModal(props: SubSelectModalProps) {
  const {
    trigger,
    title,
    initialSelection,
    request,
    onConfirm,
    hideCredentialToggle,
    onFormSubmissionError,
  } = props;
  const modalRef = useRef<HTMLIonModalElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [allowCredOnly, setAllowCredOnly] = useState(false);
  const [selected, setSelected] = useState<SubModel[]>([]);
  const { isError, subs } = useSubs({
    request,
    query: searchTerm,
  });

  const isChecked = (value: SubModel) => {
    return selected.find((item) => item.id === value.id) !== undefined;
  };

  const onCheckboxChange = (e: CustomEvent) => {
    const { checked, value } = e.detail;
    return checked
      ? setSelected((selected) => [...selected, value])
      : setSelected(selected.filter((item) => item.id !== value.id));
  };

  const updateSubSelectErrorState = () => {
    onFormSubmissionError((error: Error) => ({
      ...error,
      subs: selected.length === 0 ? translate("SUB_SELECT_REQUIRED_ERROR") : undefined,
    }));
  };

  const onModalDismiss = () => {
    setSelected(initialSelection.selectedSubs);
    setAllowCredOnly(initialSelection.isCredentialRequired);

    // If no subs are selected after resetting, update the error state
    if (initialSelection.selectedSubs.length === 0) {
      updateSubSelectErrorState();
    }
    modalRef.current?.dismiss();
  };

  const onModalConfirm = () => {
    updateSubSelectErrorState();
    onConfirm({ selectedSubs: selected, isCredentialRequired: allowCredOnly });
    modalRef.current?.dismiss();
  };

  useEffect(() => {
    const derivedCredValue =
      initialSelection.isCredentialedPositionSelected ?? initialSelection.isCredentialRequired;
    setSelected(initialSelection.selectedSubs);
    setAllowCredOnly(derivedCredValue);
  }, [
    initialSelection.isCredentialRequired,
    initialSelection.selectedSubs,
    initialSelection.isCredentialedPositionSelected,
  ]);

  const internalSubs = subs?.filter((sub) => sub.isInternal);

  return (
    <IonModal
      trigger={trigger}
      ref={modalRef}
      onIonModalDidDismiss={() => setSearchTerm("")}
      backdropDismiss={false}
    >
      <>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton color="danger" onClick={onModalDismiss}>
                {translate("LABEL_CANCEL")}
              </IonButton>
            </IonButtons>
            <IonTitle>{title}</IonTitle>
            <IonButtons slot="end">
              <IonButton color="primary" onClick={onModalConfirm} disabled={isError}>
                {translate("LABEL_DONE")}
              </IonButton>
            </IonButtons>
          </IonToolbar>
          <IonToolbar>
            <IonSearchbar
              debounce={500}
              onIonInput={(e) => {
                setSearchTerm(e?.target?.value || "");
              }}
              showClearButton="always"
            />
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {initialSelection.isCredentialedPositionSelected && (
            <Alert css={{ margin: "16px" }} color="info">
              {translate("SUB_SELECT_ALERT_CREDENTIALED")}
            </Alert>
          )}
          {!hideCredentialToggle && (
            <IonList lines="full" className="pt-0">
              <IonItem>
                <IonLabel className="font-medium">
                  {translate("SUB_SELECT_LABEL_ALLOW_CRED_ONLY")}
                </IonLabel>
                <IonToggle
                  aria-label="credential-permit-toggle"
                  slot="end"
                  onIonChange={(e: CustomEvent) => {
                    setAllowCredOnly(e.detail.checked);

                    if (e.detail.checked) {
                      const credentialedSubs = selected.filter((sub) => sub.isCredentialed);
                      setSelected(credentialedSubs);
                    }
                  }}
                  checked={allowCredOnly}
                />
              </IonItem>
            </IonList>
          )}
          {isError ? (
            <div className="flex justify-center mt-4">
              <span>{translate("GENERAL_ERROR")}</span>
            </div>
          ) : !subs ? (
            <div className="flex justify-center mt-4">
              <IonSpinner name="dots" />
            </div>
          ) : (
            <>
              {/* Internal Subs */}
              {internalSubs && internalSubs.length > 0 && (
                <Section
                  header={translate("SUB_SELECT_LABEL_INTERNAL_SECTION")}
                  data={internalSubs}
                  isChecked={isChecked}
                  onChange={onCheckboxChange}
                  extraData={{ allowCredOnly }}
                />
              )}
              {/* My Favorites Section */}
              <Section
                header={translate("SUB_SELECT_LABEL_FAVORITES_SECTION")}
                data={subs.filter((sub) => sub.isSchoolFavorited)}
                isChecked={isChecked}
                onChange={onCheckboxChange}
                extraData={{ allowCredOnly }}
              />
              {/* All Subs Section */}
              <Section
                header={translate("SUB_SELECT_LABEL_ALL_SECTION")}
                data={subs}
                isChecked={isChecked}
                onChange={onCheckboxChange}
                extraData={{ allowCredOnly }}
              />
            </>
          )}
        </IonContent>
      </>
    </IonModal>
  );
}
